export type AbortScopeEndReason = "timeout" | "cancelled";

/**
 * A unit of cancellable work with an optional time limit. Its signal is handed to fetch/axios so the
 * request itself stops; `ended` lets callers race work that takes no signal (token lookups), and keeps
 * the time limit working where AbortController does not exist.
 */
export interface IAbortScope {
    /** Aborts when the scope ends. Undefined where AbortController is unavailable. */
    readonly signal: AbortSignal | undefined;
    /** Resolves once the scope times out, is cancelled or its parent ends. Never rejects. */
    readonly ended: Promise<void>;
    /** Why the scope ended; undefined while it is still running. */
    endReason: () => AbortScopeEndReason | undefined;
    cancel: () => void;
    /** Releases the timer and the parent link without ending the scope. */
    dispose: () => void;
}

/** A scope that times out after `timeoutMs` (none when omitted) and is cancelled when `parent` ends. */
export const createAbortScope = (timeoutMs?: number, parent?: IAbortScope): IAbortScope => {
    const controller: AbortController | undefined = typeof AbortController !== "undefined" ? new AbortController() : undefined;
    let reason: AbortScopeEndReason | undefined;
    let disposed: boolean = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let resolveEnded!: () => void;
    const ended = new Promise<void>(resolve => { resolveEnded = resolve; });

    const end = (why: AbortScopeEndReason): void => {
        if (reason || disposed) return;
        reason = why;
        if (timer !== undefined) clearTimeout(timer);
        controller?.abort();
        resolveEnded();
    };

    if (parent?.endReason()) {
        end("cancelled");
    } else {
        if (timeoutMs !== undefined) timer = setTimeout(() => end("timeout"), timeoutMs);
        parent?.ended.then(() => end("cancelled"));
    }

    return {
        signal: controller?.signal,
        ended,
        endReason: () => reason,
        cancel: () => end("cancelled"),
        dispose: () => {
            if (timer !== undefined) clearTimeout(timer);
            disposed = true;
        },
    };
};

export const SCOPE_ENDED = Object.freeze({ scopeEnded: true });

export const isScopeEnded = (value: unknown): value is typeof SCOPE_ENDED => value === SCOPE_ENDED;

/** The work's outcome, or SCOPE_ENDED when the scope ends first. Without a scope, just the work. */
export const raceEnded = <T>(work: Promise<T>, scope: IAbortScope | undefined): Promise<T | typeof SCOPE_ENDED> =>
    scope ? Promise.race([work, scope.ended.then(() => SCOPE_ENDED)]) : work;
